import {Pinia} from 'pinia'
import {RouteLocationNormalized} from 'vue-router'
import {useAuthStore} from '@toolify/client/src/stores/AuthStore/useAuthStore'
import {useCurrentAppPlatform} from '@toolify/client/src/modules/platform/composables/useCurrentAppPlatform'
import {setLanguage} from '@toolify/client/src/setup/i18n'
import {ClientRouteParamType} from '@toolify/client/src/setup/router/enum/ClientRouteParamType'
import {resolveGuestLocaleType} from '@toolify/client/src/modules/authentication/functions/resolveGuestLocaleType'
import {resolveGuestThemeType} from '@toolify/client/src/modules/authentication/functions/resolveGuestThemeType'
import {getGuestThemeQueryValue} from '@toolify/client/src/modules/authentication/functions/getGuestThemeQueryValue'
import {getStoredGuestLocale} from '@toolify/client/src/modules/authentication/functions/getStoredGuestLocale'
import {setStoredGuestTheme} from '@toolify/client/src/modules/authentication/functions/setStoredGuestTheme'
import {getPathWithoutLocalePrefix} from '@toolify/client/src/modules/authentication/functions/getPathWithoutLocalePrefix'
import {KnownError} from '@toolify/server/src/middleware/ApiResponseMiddleware/types/KnownError'
import {ApiException} from '@toolify/server/src/middleware/ApiResponseMiddleware/errors/ApiException'
import * as Sentry from '@sentry/vue'

export function applyGuestAppearanceRouterHookFactory(pinia: Pinia) {
  return async function applyGuestAppearanceRouterHook(to: RouteLocationNormalized) {
    const authStore = useAuthStore(pinia)
    try {
      await authStore.initializeAuth()
    } catch(error) {
      const apiException = error as ApiException
      if(apiException.name !== KnownError.INVALID_TOKEN) {
        Sentry.captureException(error)
      }
    }

    const routeLocale = to.params[ClientRouteParamType.Locale]
    const hasRouteLocale = typeof routeLocale === 'string' && routeLocale

    if(authStore.isLoggedIn) {
      if(!hasRouteLocale) {
        return
      }
      return {
        path: getPathWithoutLocalePrefix(to.path, routeLocale),
        query: to.query,
        hash: to.hash,
      }
    }

    const {currentAppPlatformType} = useCurrentAppPlatform()
    const locale = resolveGuestLocaleType({
      routeLocale,
      storedLocale: getStoredGuestLocale(),
      appPlatformType: currentAppPlatformType,
    })
    setLanguage(locale)

    const queryTheme = getGuestThemeQueryValue(to.query)
    if(queryTheme) {
      setStoredGuestTheme(resolveGuestThemeType({
        queryTheme,
        appPlatformType: currentAppPlatformType,
      }))
    }
  }
}
